// PRD-3334 v2.2: every PLG request proves it comes from the browser that started the session.
// A non-extractable ECDSA P-256 key lives in IndexedDB; each request carries a fresh signed
// proof (compact JWS, ES256) of method + URL. A copied token or magic link is useless elsewhere.

import { clockNowMs } from "plg/auth/clockOffset";

export interface DeviceKeyStore {
  get: () => Promise<CryptoKeyPair | null>;
  put: (pair: CryptoKeyPair) => Promise<void>;
}

const DB_NAME = "plg-device";
const STORE = "keys";
const KEY = "device";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** IndexedDB-backed store; a CryptoKey is structured-cloneable, so the private key never leaves as bytes. */
export const indexedDbKeyStore: DeviceKeyStore = {
  async get() {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const req = db.transaction(STORE, "readonly").objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve((req.result as CryptoKeyPair) || null);
      req.onerror = () => reject(req.error);
    });
  },
  async put(pair) {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(pair, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  },
};

let store: DeviceKeyStore | null = typeof indexedDB !== "undefined" ? indexedDbKeyStore : null;
let memoryPair: CryptoKeyPair | null = null;
let pending: Promise<CryptoKeyPair> | null = null;

/** "persistent": the key is in IndexedDB and shared by tabs; "memory": this tab only. */
export type DeviceKeyPersistence = "unknown" | "persistent" | "memory";
let persistence: DeviceKeyPersistence = "unknown";
export const getDeviceKeyPersistence = (): DeviceKeyPersistence => persistence;

/** Tests (and environments without IndexedDB) swap the store; null keeps the key in memory for this tab. */
export function setDeviceKeyStore(next: DeviceKeyStore | null): void {
  store = next;
  memoryPair = null;
  pending = null;
  persistence = "unknown";
  prepared.clear();
}

const subtle = () => globalThis.crypto.subtle;

async function loadOrCreate(): Promise<CryptoKeyPair> {
  if (memoryPair) return memoryPair;
  if (store) {
    try {
      const existing = await store.get();
      if (existing) {
        memoryPair = existing;
        persistence = "persistent";
        return existing;
      }
    } catch {
      // IndexedDB blocked (private mode, quota): fall back to a key for this tab only.
      store = null;
    }
  }
  const pair = (await subtle().generateKey({ name: "ECDSA", namedCurve: "P-256" }, false, ["sign", "verify"])) as CryptoKeyPair;
  if (store) {
    try {
      await store.put(pair);
      // Read back: if another tab saved its key at the same moment, the stored one wins for everyone.
      const stored = await store.get();
      if (stored) {
        memoryPair = stored;
        persistence = "persistent";
        return stored;
      }
    } catch {
      // the save failed: this key lives only in this tab
    }
  }
  memoryPair = pair;
  persistence = "memory";
  return pair;
}

export function getDeviceKeyPair(): Promise<CryptoKeyPair> {
  if (!pending) pending = loadOrCreate().finally(() => (pending = null));
  return pending;
}

const b64url = (bytes: ArrayBuffer | Uint8Array): string => {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  arr.forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};
const b64urlJson = (v: unknown) => b64url(new TextEncoder().encode(JSON.stringify(v)));

function randomJti(): string {
  const bytes = new Uint8Array(18);
  globalThis.crypto.getRandomValues(bytes);
  return b64url(bytes);
}

/** The URL the proof covers: absolute, without query or fragment. */
export function proofTarget(url: string): string {
  const u = new URL(url, window.location.origin);
  return `${u.origin}${u.pathname}`;
}

export interface ProofOptions {
  /** The PLG access token the request carries; its SHA-256 goes into the proof as `ath`. */
  accessToken?: string | null;
}

/** A fresh compact JWS for one request. WebCrypto ECDSA signatures are already raw r||s (64 bytes). */
export async function createPlgProof(method: string, url: string, opts: ProofOptions = {}): Promise<string> {
  const { privateKey, publicKey } = await getDeviceKeyPair();
  const jwk = await subtle().exportKey("jwk", publicKey);
  const header = { typ: "plg-proof+jwt", alg: "ES256", jwk: { kty: "EC", crv: "P-256", x: jwk.x, y: jwk.y } };
  const payload: Record<string, unknown> = {
    htm: method.toUpperCase(),
    htu: proofTarget(url),
    iat: Math.floor(clockNowMs() / 1000),
    jti: randomJti(),
  };
  if (opts.accessToken) {
    payload.ath = b64url(await subtle().digest("SHA-256", new TextEncoder().encode(opts.accessToken)));
  }
  const input = `${b64urlJson(header)}.${b64urlJson(payload)}`;
  const sig = await subtle().sign({ name: "ECDSA", hash: "SHA-256" }, privateKey, new TextEncoder().encode(input));
  return `${input}.${b64url(sig)}`;
}

export const PLG_PROOF_HEADER = "PLG-Proof";

/** Headers to merge into a PLG request; on any crypto failure the request goes without (the server rejects it). */
export async function plgProofHeaders(method: string, url: string, opts: ProofOptions = {}): Promise<Record<string, string>> {
  try {
    return { [PLG_PROOF_HEADER]: await createPlgProof(method, url, opts) };
  } catch {
    return {};
  }
}

// Requests sent while the page unloads (keepalive analytics) can't wait for WebCrypto, so one proof
// per request target is signed ahead of time and handed out synchronously, once.
const PREPARED_MAX_AGE_MS = 40_000; // under the server's 60 s skew window
const prepared = new Map<string, { proof: string; token: string; at: number }>();
const preparedKey = (method: string, url: string) => `${method.toUpperCase()} ${proofTarget(url)}`;

export async function preparePlgProof(method: string, url: string, accessToken: string | null): Promise<void> {
  try {
    const proof = await createPlgProof(method, url, { accessToken });
    prepared.set(preparedKey(method, url), { proof, token: accessToken || "", at: Date.now() });
  } catch {
    // nothing prepared: the unload request goes without a proof and the server ignores it
  }
}

export function takePreparedProof(method: string, url: string, accessToken: string | null): string | null {
  const key = preparedKey(method, url);
  const entry = prepared.get(key);
  if (!entry || entry.token !== (accessToken || "")) return null;
  prepared.delete(key);
  return Date.now() - entry.at > PREPARED_MAX_AGE_MS ? null : entry.proof;
}
